/**
 * Sentry + PostHog observability bootstrap.
 * Both tools are no-ops when their env var is not set (safe in dev).
 */
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';
const APP_ENV = process.env.NODE_ENV === 'production' ? 'production' : 'preview';

// Stored reference to OUR PostHog instance (named 'akali').
// Resilient to 3rd-party scripts overwriting window.posthog.
let _akali = null;

export const initObservability = () => {
  // --- Sentry ---
  if (SENTRY_DSN) {
    try {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: APP_ENV,
        release: 'akali-frontend@0.2.0',
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 0.1,
        // LGPD: we explicitly redact patient-related content via sendDefaultPii=false
        sendDefaultPii: false,
        // Don't capture console.log
        beforeSend(event) {
          // Redact any URL path segments that look like /patients/<id> or /consultations/<id>
          if (event.request?.url) {
            event.request.url = event.request.url
              .replace(/\/patients\/[a-f0-9-]{8,}/gi, '/patients/[redacted]')
              .replace(/\/consultations\/[a-f0-9-]{8,}/gi, '/consultations/[redacted]');
          }
          return event;
        },
      });
      // Expose Sentry on window for QA diagnostics + e2e proof (read-only access).
      if (typeof window !== 'undefined') window.Sentry = Sentry;
      // eslint-disable-next-line no-console
      console.log('[observability] Sentry initialized');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[observability] Sentry init failed:', e);
    }
  }

  // --- PostHog (named instance to avoid clashes with other tracking on the page) ---
  if (POSTHOG_KEY) {
    try {
      // Initialize as a named instance and STORE the returned reference directly.
      // This is robust against `window.posthog` being overwritten by 3rd-party scripts.
      _akali = posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: true,
        // Always capture events even before identify() — the project has
        // `defaultIdentifiedOnly: true` which would drop anonymous events.
        person_profiles: 'always',
        // Allow QA / headless smoke tests to capture (real-world impact: also
        // prevents legit users on alt-Chromium browsers from being silently dropped).
        opt_out_useragent_filter: true,
        persistence: 'localStorage+cookie',
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['button', 'a', 'label'],
        },
        mask_all_text: false,
        mask_all_element_attributes: false,
        session_recording: { maskAllInputs: true, maskTextSelector: '[data-posthog-mask]' },
        disable_session_recording: true,
        loaded: (instance) => {
          // Always prefer the loaded callback's reference (most reliable)
          _akali = instance;
          // eslint-disable-next-line no-console
          console.log('[observability] PostHog (akali) initialized -> ' + POSTHOG_HOST);
          // Also expose for debugging from devtools
          if (typeof window !== 'undefined') window.__akali_posthog = instance;
        },
      }, 'akali');
      if (typeof window !== 'undefined') window.__akali_posthog = _akali;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[observability] PostHog init failed:', e);
    }
  }
};

// Always grab OUR instance via stored reference; falls back to named global if needed.
const ph = () => {
  if (_akali) return _akali;
  try {
    if (typeof posthog !== 'undefined' && posthog['akali']) return posthog['akali'];
  } catch (e) {
    // Defensive: named instance lookup is best-effort
    if (process.env.NODE_ENV !== 'production') console.warn('[observability] posthog lookup:', e);
  }
  return null;
};

// Wrappers used by the app to track business events (no-op if tools disabled)
export const track = (event, properties = {}) => {
  const inst = ph();
  if (!inst) return;
  try {
    // LGPD: never send patient data. Caller is responsible, but we strip known PII keys.
    const safe = { ...properties };
    ['patient_name', 'name', 'cpf', 'patient_id', 'phone', 'email_personal'].forEach((k) => {
      if (k in safe) delete safe[k];
    });
    inst.capture(event, safe);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[observability] track failed:', event, e);
  }
};

// Explicit PostHog $pageview capture for SPA route changes.
export const capturePageview = (pathname) => {
  const inst = ph();
  if (!inst) return;
  try {
    inst.capture('$pageview', {
      $current_url: typeof window !== 'undefined' ? window.location.href : pathname,
      pathname: pathname || (typeof window !== 'undefined' ? window.location.pathname : ''),
    });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[observability] pageview failed:', e);
  }
};

export const identify = (userId, traits = {}) => {
  const inst = ph();
  if (!inst) return;
  try {
    const safeTraits = { ...traits };
    if (safeTraits.email) {
      safeTraits.email_domain = String(safeTraits.email).split('@')[1] || 'unknown';
    }
    inst.identify(userId, safeTraits);
    // Also set the user context in Sentry
    if (SENTRY_DSN) Sentry.setUser({ id: userId, email: safeTraits.email });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[observability] identify failed:', e);
  }
};

export const resetAnalytics = () => {
  const inst = ph();
  if (inst) {
    try {
      inst.reset();
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('[observability] posthog reset failed:', e);
    }
  }
  if (SENTRY_DSN) {
    try {
      Sentry.setUser(null);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('[observability] sentry reset failed:', e);
    }
  }
};

export const reportError = (error, context = {}) => {
  if (!SENTRY_DSN) return;
  try {
    Sentry.captureException(error, { extra: context });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[observability] reportError failed:', e);
  }
};
